'use client';

import classNames from 'classnames';
import React, { Fragment, useEffect, useState } from 'react';

import { StrapiCityRouteDatum } from '@/apis/strapi';
import { doTranslation } from '@/app/i18n/client';
import Select from '@/components/global/forms/select';
import { localeSpecificPath } from '@/utils/localeUtils';

import styles from '@/components/schedule-results/popular-bus-tickets-and-routes.module.scss';

type Props = {
  originCityName: string;
  originCitySlug: string;
  originStateAbbrev: string;
  destinationCityName: string;
  destinationCitySlug: string;
  destinationStateAbbrev: string;
  lng: string;
  originCityRoutes: StrapiCityRouteDatum[];
  destinationCityRoutes: StrapiCityRouteDatum[];
};

const PopularBusTicketsAndRoutes = (props: Props): React.JSX.Element => {
  const {
    lng,
    originCityName,
    originCitySlug,
    originStateAbbrev,
    destinationCityName,
    destinationCitySlug,
    destinationStateAbbrev,
    originCityRoutes,
    destinationCityRoutes
  } = props;

  const { t } = doTranslation(props.lng);

  const [activeTab, setActiveTab] = useState(
    originCityRoutes?.length ? 'origin' : destinationCityRoutes?.length ? 'destination' : ''
  );
  const [activeCity, setActiveCity] = useState('New York');
  const [isTablet, setIsTablet] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 1279 && window.innerWidth > 600
  );
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1279 && window.innerWidth > 600);
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isTablet, isMobile]);

  const getPopularBusTickets = () => {
    const cityRoutes =
      activeTab === 'origin'
        ? originCityRoutes
        : activeTab === 'destination'
        ? destinationCityRoutes
        : originCityRoutes;

    if (!cityRoutes?.length) {
      return [];
    }

    const values: {
      from: { [key: string]: string } | null | undefined;
      to: { [key: string]: string } | null | undefined;
    } = {
      from: null,
      to: null
    };

    const startColumn: { [key: string]: string }[] = [];
    const centerColumn: { [key: string]: string }[] = [];
    const endColumn: { [key: string]: string }[] = [];
    let count = 0;
    cityRoutes.forEach((route) => {
      const { from, to } = values;
      count++;
      const fromCityName = route.attributes.origin_city?.data.attributes.city_name || originCityName;
      const fromCitySlug = route.attributes.origin_city?.data.attributes.city_slug || originCitySlug;
      const toCityName = route.attributes.destination_city?.data.attributes.city_name || destinationCityName;
      const toCitySlug = route.attributes.destination_city?.data.attributes.city_slug || destinationCitySlug;
      const value = {
        from: fromCityName,
        to: toCityName,
        url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
          `/schedules/bus-from-${fromCitySlug}-to-${toCitySlug}/`,
          lng
        )}`
      };
      const colMod = count % 3;
      if (colMod === 1) {
        startColumn.push(value);
      }
      if (colMod === 2) {
        centerColumn.push(value);
      }
      if (colMod === 0) {
        endColumn.push(value);
      }
    });

    return { startColumn, centerColumn, endColumn };
  };

  const getPopularRoutes = () => {
    return {
      tabs: [
        // NEW YORK
        {
          name: 'New York',
          slug: 'new-york',
          routes: {
            startColumn: [
              {
                from: 'Montreal',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-montreal-qc-to-new-york-ny/`, lng)}`
              },
              {
                from: 'Albany',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-albany-ny-to-new-york-ny/`, lng)}`
              },
              {
                from: 'New Paltz',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-new-paltz-ny-to-new-york-ny/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'New York',
                to: 'Toronto',
                url: `${localeSpecificPath(`/schedules/bus-from-new-york-ny-to-toronto-on/`, lng)}`
              },
              {
                from: 'Kingston',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-kingston-ny-to-new-york-ny/`, lng)}`
              },
              {
                from: 'Boston',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-new-york-ny/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Buffalo',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-buffalo-ny-to-new-york-ny/`, lng)}`
              },
              {
                from: 'New York',
                to: 'Ithaca',
                url: `${localeSpecificPath(`/schedules/bus-from-new-york-ny-to-ithaca-ny/`, lng)}`
              },
              {
                from: 'Syracuse',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-syracuse-ny-to-new-york-ny/`, lng)}`
              }
            ]
          }
        },
        // MONTREAL
        {
          name: 'Montreal',
          slug: 'montreal',
          routes: {
            startColumn: [
              {
                from: 'Montreal',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-montreal-qc-to-new-york-ny/`, lng)}`
              },
              {
                from: 'Philadelphia',
                to: 'Montreal',
                url: `${localeSpecificPath(`/schedules/bus-from-philadelphia-pa-to-montreal-qc/`, lng)}`
              },
              {
                from: 'Washington',
                to: 'Montreal',
                url: `${localeSpecificPath(`/schedules/bus-from-washington-dc-to-montreal-qc/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'Montreal',
                to: 'Albany',
                url: `${localeSpecificPath(`/schedules/bus-from-montreal-qc-to-albany-ny/`, lng)}`
              },
              {
                from: 'Plattsburgh',
                to: 'Montreal',
                url: `${localeSpecificPath(`/schedules/bus-from-plattsburgh-ny-to-montreal-qc/`, lng)}`
              },
              {
                from: 'New York',
                to: 'Montreal',
                url: `${localeSpecificPath(`/schedules/bus-from-new-york-ny-to-montreal-qc/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Montreal',
                to: 'Philadelphia',
                url: `${localeSpecificPath(`/schedules/bus-from-montreal-qc-to-philadelphia-pa/`, lng)}`
              },
              {
                from: 'Montreal',
                to: 'Washington',
                url: `${localeSpecificPath(`/schedules/bus-from-montreal-qc-to-washington-dc/`, lng)}`
              },
              {
                from: 'Albany',
                to: 'Montreal',
                url: `${localeSpecificPath(`/schedules/bus-from-albany-ny-to-montreal-qc/`, lng)}`
              }
            ]
          }
        },
        // CHICAGO
        {
          name: 'Chicago',
          slug: 'chicago',
          routes: {
            startColumn: [
              {
                from: 'Iowa City',
                to: 'Chicago',
                url: `${localeSpecificPath(`/schedules/bus-from-iowa-city-ia-to-chicago-il/`, lng)}`
              },
              {
                from: 'Des Moines',
                to: 'Chicago',
                url: `${localeSpecificPath(`/schedules/bus-from-des-moines-ia-to-chicago-il/`, lng)}`
              },
              {
                from: 'Saint Louis',
                to: 'Chicago',
                url: `${localeSpecificPath(`/schedules/bus-from-saint-louis-mo-to-chicago-il/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'Cedar Rapids',
                to: 'Chicago',
                url: `${localeSpecificPath(`/schedules/bus-from-cedar-rapids-ia-to-chicago-il/`, lng)}`
              },
              {
                from: 'Chicago',
                to: 'Des Moines',
                url: `${localeSpecificPath(`/schedules/bus-from-chicago-il-to-des-moines-ia/`, lng)}`
              },
              {
                from: 'Chicago',
                to: 'Minneapolis',
                url: `${localeSpecificPath(`/schedules/bus-from-chicago-il-to-minneapolis-mn/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Chicago',
                to: 'Kansas City',
                url: `${localeSpecificPath(`/schedules/bus-from-chicago-il-to-kansas-city-mo/`, lng)}`
              },
              {
                from: 'Chicago',
                to: 'Davenport',
                url: `${localeSpecificPath(`/schedules/bus-from-chicago-il-to-davenport-ia/`, lng)}`
              },
              {
                from: 'Chicago',
                to: 'Cedar Rapids',
                url: `${localeSpecificPath(`/schedules/bus-from-chicago-il-to-cedar-rapids-ia/`, lng)}`
              }
            ]
          }
        },
        // SEATTLE
        {
          name: 'Seattle',
          slug: 'seattle',
          routes: {
            startColumn: [
              {
                from: 'Seattle',
                to: 'Billings',
                url: `${localeSpecificPath(`/schedules/bus-from-seattle-wa-to-billings-mt/`, lng)}`
              },
              {
                from: 'Seattle',
                to: 'Moscow',
                url: `${localeSpecificPath(`/schedules/bus-from-seattle-wa-to-moscow-id/`, lng)}`
              },
              {
                from: 'Seattle',
                to: 'Spokane',
                url: `${localeSpecificPath(`/schedules/bus-from-seattle-wa-to-spokane-wa/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'Seattle',
                to: 'Eugene',
                url: `${localeSpecificPath(`/schedules/bus-from-seattle-wa-to-eugene-or/`, lng)}`
              },
              {
                from: 'Seattle',
                to: 'Butte',
                url: `${localeSpecificPath(`/schedules/bus-from-seattle-wa-to-butte-mt/`, lng)}`
              },
              {
                from: 'Billings',
                to: 'Seattle',
                url: `${localeSpecificPath(`/schedules/bus-from-billings-mt-to-seattle-wa/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Spokane',
                to: 'Seattle',
                url: `${localeSpecificPath(`/schedules/bus-from-spokane-wa-to-seattle-wa/`, lng)}`
              },
              {
                from: 'Moscow',
                to: 'Seattle',
                url: `${localeSpecificPath(`/schedules/bus-from-moscow-id-to-seattle-wa/`, lng)}`
              },
              {
                from: 'Eugene',
                to: 'Seattle',
                url: `${localeSpecificPath(`/schedules/bus-from-eugene-or-to-seattle-wa/`, lng)}`
              }
            ]
          }
        },
        // BOSTON
        {
          name: 'Boston',
          slug: 'boston',
          routes: {
            startColumn: [
              {
                from: 'New York',
                to: 'Boston',
                url: `${localeSpecificPath(`/schedules/bus-from-new-york-ny-to-boston-ma/`, lng)}`
              },
              {
                from: 'Albany',
                to: 'Boston',
                url: `${localeSpecificPath(`/schedules/bus-from-albany-ny-to-boston-ma/`, lng)}`
              },
              {
                from: 'Kingston',
                to: 'Boston',
                url: `${localeSpecificPath(`/schedules/bus-from-kingston-ny-to-boston-ma/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'Boston',
                to: 'Burlington',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-burlington-vt/`, lng)}`
              },
              {
                from: 'Boston',
                to: 'New Paltz',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-new-paltz-ny/`, lng)}`
              },
              {
                from: 'Boston',
                to: 'Hartford',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-hartford-ct/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Boston',
                to: 'Providence',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-providence-ri/`, lng)}`
              },
              {
                from: 'Boston',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-new-york-ny/`, lng)}`
              },
              {
                from: 'Boston',
                to: 'Albany',
                url: `${localeSpecificPath(`/schedules/bus-from-boston-ma-to-albany-ny/`, lng)}`
              }
            ]
          }
        },
        // TORONTO
        {
          name: 'Toronto',
          slug: 'toronto',
          routes: {
            startColumn: [
              {
                from: 'New York',
                to: 'Toronto',
                url: `${localeSpecificPath(`/schedules/bus-from-new-york-ny-to-toronto-on/`, lng)}`
              },
              {
                from: 'Toronto',
                to: 'Detroit',
                url: `${localeSpecificPath(`/schedules/bus-from-toronto-on-to-detroit-mi/`, lng)}`
              },
              {
                from: 'Toronto',
                to: 'London',
                url: `${localeSpecificPath(`/schedules/bus-from-toronto-on-to-london-on/`, lng)}`
              }
            ],
            centerColumn: [
              {
                from: 'Buffalo',
                to: 'Toronto',
                url: `${localeSpecificPath(`/schedules/bus-from-buffalo-ny-to-toronto-on/`, lng)}`
              },
              {
                from: 'Toronto',
                to: 'Philadelphia',
                url: `${localeSpecificPath(`/schedules/bus-from-toronto-on-to-philadelphia-pa/`, lng)}`
              },
              {
                from: 'Detroit',
                to: 'Toronto',
                url: `${localeSpecificPath(`/schedules/bus-from-detroit-mi-to-toronto-on/`, lng)}`
              }
            ],
            endColumn: [
              {
                from: 'Toronto',
                to: 'New York',
                url: `${localeSpecificPath(`/schedules/bus-from-toronto-on-to-new-york-ny/`, lng)}`
              },
              {
                from: 'Toronto',
                to: 'Buffalo',
                url: `${localeSpecificPath(`/schedules/bus-from-toronto-on-to-buffalo-ny/`, lng)}`
              },
              {
                from: 'Philadelphia',
                to: 'Toronto',
                url: `${localeSpecificPath(`/schedules/bus-from-philadelphia-pa-to-toronto-on/`, lng)}`
              }
            ]
          }
        }
      ]
    };
  };

  const handleKeyDown = (event: React.KeyboardEvent, currentTab: string, component: string) => {
    const tabs = component === 'tickets' ? ['origin', 'destination'] : popularRoutes.tabs.map((tab) => tab.name);
    const currentTabIndex = tabs.indexOf(currentTab);
    const totalTabs = tabs.length;
    let flag = false;

    if (!(originCityRoutes?.length && destinationCityRoutes?.length) && component === 'tickets') {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        if (currentTabIndex === 0) {
          setSelectedTab(tabs[totalTabs - 1], component);
        } else {
          setSelectedTab(tabs[currentTabIndex - 1], component);
        }
        flag = true;
        break;
      case 'ArrowRight':
        if (currentTabIndex === totalTabs - 1) {
          setSelectedTab(tabs[0], component);
        } else {
          setSelectedTab(tabs[currentTabIndex + 1], component);
        }
        flag = true;
        break;
      case 'Home':
        setSelectedTab(tabs[0], component);
        flag = true;
        break;
      case 'End':
        setSelectedTab(tabs[totalTabs - 1], component);
        flag = true;
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const setSelectedTab = (tab: string, component: string) => {
    if (component === 'tickets') {
      setActiveTab(tab);
      document.getElementById(`tab-${tab}`)?.focus();
    }
    if (component === 'routes') {
      setActiveCity(tab);
      const slug = popularRoutes.tabs.find((t) => t.name === tab)?.slug;
      document.getElementById(`tab-${slug}`)?.focus();
    }
  };

  const popularBusTickets = getPopularBusTickets();
  const popularRoutes = getPopularRoutes();
  const origin = [originCityName, originStateAbbrev || ''].join(', ');
  const destination = [destinationCityName, destinationStateAbbrev || ''].join(', ');

  const renderPopularBusTicketsMobileView = () => {
    return (
      <div className={styles.content}>
        <div className={styles.column}>
          <div className={styles.block}>
            <div aria-labelledby="popular-bus-tickets" className={styles.tabs} role="tablist">
              {originCityRoutes?.length ? (
                <div className={styles.tab}>
                  <div className={styles.frame}>
                    <button
                      aria-controls={'tabpanel-origin'}
                      aria-selected={activeTab === 'origin'}
                      className={classNames({
                        [styles.text]: true,
                        [styles.inactive]: activeTab !== 'origin'
                      })}
                      id={'tab-origin'}
                      onClick={() => setActiveTab('origin')}
                      onKeyDown={(event) => handleKeyDown(event, 'origin', 'tickets')}
                      role="tab"
                      tabIndex={activeTab === 'origin' ? 0 : -1}
                      type="button"
                    >
                      <span>{t('From')}</span>
                      <span>{origin}</span>
                    </button>
                  </div>
                  <div
                    className={classNames({
                      [styles.line]: true,
                      [styles.inactive]: activeTab !== 'origin'
                    })}
                  ></div>
                </div>
              ) : null}
              {originCityRoutes?.length && destinationCityRoutes?.length ? (
                <div className={styles.divider}></div>
              ) : null}
              {destinationCityRoutes?.length ? (
                <div className={styles.tab}>
                  <div className={styles.frame}>
                    <button
                      aria-controls={'tabpanel-destination'}
                      aria-selected={activeTab === 'destination'}
                      className={classNames({
                        [styles.text]: true,
                        [styles.inactive]: activeTab !== 'destination'
                      })}
                      id={'tab-destination'}
                      onClick={() => setActiveTab('destination')}
                      onKeyDown={(event) => handleKeyDown(event, 'destination', 'tickets')}
                      role="tab"
                      tabIndex={activeTab === 'destination' ? 0 : -1}
                      type="button"
                    >
                      <span>{t('Going To')}</span>
                      <span>{destination}</span>
                    </button>
                  </div>
                  <div
                    className={classNames({
                      [styles.line]: true,
                      [styles.inactive]: activeTab !== 'destination'
                    })}
                  ></div>
                </div>
              ) : null}
            </div>
          </div>
          <div aria-labelledby={`tab-${activeTab}`} id={`tabpanel-${activeTab}`} role="tabpanel">
            <ul className={styles.options}>
              {Object.entries(popularBusTickets).map(([_, values]) =>
                values.map((value, _) => (
                  <li key={_}>
                    <a className={styles.route} href={value.url} target="_blank">
                      {value.from} <span className={styles.to}>{t('to')}</span> {value.to}
                    </a>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderPopularRoutesMobileView = () => {
    const activePopularRoutes = popularRoutes.tabs.find((tab) => tab.name === activeCity)?.routes;
    return (
      <div className={styles.content}>
        <div className={styles.column}>
          <div
            className={classNames({
              [styles.block]: true,
              [styles.select]: true
            })}
          >
            <Select
              defaultValue={popularRoutes.tabs
                .map((tab) => ({ label: tab.name, value: tab.name }))
                .find((option) => option.value === activeCity)}
              id="popular-routes-cities"
              isSearchable={false}
              name="react-select-popular-routes-cities"
              onChange={(selectedOption: { value: string }) => {
                setActiveCity(selectedOption.value);
              }}
              options={popularRoutes.tabs.map((tab) => ({ label: tab.name, value: tab.name }))}
            />
          </div>
          <ul className={styles.options}>
            {activePopularRoutes &&
              Object.entries(activePopularRoutes).map(([_, values]) =>
                values.map((value, _) => (
                  <li key={_}>
                    <a className={styles.route} href={value.url} target="_blank">
                      {value.from} <span className={styles.to}>{t('to')}</span> {value.to}
                    </a>
                  </li>
                ))
              )}
          </ul>
        </div>
      </div>
    );
  };

  const renderPopularBusTickets = () => {
    return (
      <div aria-labelledby="popular-bus-tickets" className={styles.section} role="region">
        <div className={styles.heading}>
          <h2 className={styles.title} id="popular-bus-tickets">
            {t('Other Popular Routes for {{ origin }} and {{ destination }}', { origin, destination })}
          </h2>
        </div>
        {isMobile ? (
          renderPopularBusTicketsMobileView()
        ) : (
          <div className={styles.from}>
            <div aria-labelledby="popular-bus-tickets" className={styles.tabs} role="tablist">
              {originCityRoutes?.length ? (
                <div className={styles.tab}>
                  <div className={styles.frame}>
                    <button
                      aria-controls={'tabpanel-origin'}
                      aria-selected={activeTab === 'origin'}
                      className={classNames({
                        [styles.text]: true,
                        [styles.inactive]: activeTab !== 'origin'
                      })}
                      id={'tab-origin'}
                      onClick={() => setActiveTab('origin')}
                      onKeyDown={(event) => handleKeyDown(event, 'origin', 'tickets')}
                      role="tab"
                      tabIndex={activeTab === 'origin' ? 0 : -1}
                      type="button"
                    >
                      <span>{t('From')}</span>
                      <span>{origin}</span>
                    </button>
                  </div>
                  <div
                    className={classNames({
                      [styles.line]: true,
                      [styles.inactive]: activeTab !== 'origin'
                    })}
                  ></div>
                </div>
              ) : null}
              {originCityRoutes?.length && destinationCityRoutes?.length ? (
                <div className={styles.divider}></div>
              ) : null}
              {destinationCityRoutes?.length ? (
                <div className={styles.tab}>
                  <div className={styles.frame}>
                    <button
                      aria-controls={'tabpanel-destination'}
                      aria-selected={activeTab === 'destination'}
                      className={classNames({
                        [styles.text]: true,
                        [styles.inactive]: activeTab !== 'destination'
                      })}
                      id={'tab-destination'}
                      onClick={() => setActiveTab('destination')}
                      onKeyDown={(event) => handleKeyDown(event, 'destination', 'tickets')}
                      role="tab"
                      tabIndex={activeTab === 'destination' ? 0 : -1}
                      type="button"
                    >
                      <span>{t('Going To')}</span>
                      <span>{destination}</span>
                    </button>
                  </div>
                  <div
                    className={classNames({
                      [styles.line]: true,
                      [styles.inactive]: activeTab !== 'destination'
                    })}
                  ></div>
                </div>
              ) : null}
            </div>
            <div
              aria-labelledby={`tab-${activeTab}`}
              id={`tabpanel-${activeTab}`}
              className={styles.content}
              role="tabpanel"
            >
              {Object.entries(popularBusTickets).map(([_, values]) => (
                <ul className={styles.column} key={_}>
                  {values.map((value, _) => (
                    <li key={_}>
                      <a className={`${styles.route} gtm-other_popular_bus_routes`} href={value.url} target="_blank">
                        {value.from} <span className={styles.to}>{t('to')}</span> {value.to}
                      </a>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPopularRoutes = () => {
    const activePopularRoutes = popularRoutes.tabs.find((tab) => tab.name === activeCity)?.routes;
    return (
      <div aria-labelledby="popular-routes" className={styles.section} role="region">
        <div className={styles.heading}>
          <h2 className={styles.title} id="popular-routes">
            {t('Discover our Most Popular Routes Nationwide')}
          </h2>
        </div>
        {isMobile ? (
          renderPopularRoutesMobileView()
        ) : (
          <div className={styles.from}>
            <div className={styles.tabs} aria-labelledby="popular-routes" role="tablist">
              {popularRoutes.tabs.map((tab, index) => (
                <Fragment key={index}>
                  {index !== 0 ? (
                    <div
                      className={classNames({
                        [styles.divider]: true,
                        [styles['popular-route']]: true
                      })}
                    ></div>
                  ) : null}
                  <div className={styles.tab}>
                    <div
                      className={classNames({
                        [styles.frame]: true,
                        [styles['popular-route']]: true
                      })}
                    >
                      <button
                        aria-controls={`tabpanel-${tab.slug}`}
                        aria-selected={activeCity === tab.name}
                        className={classNames({
                          [styles.text]: true,
                          [styles['popular-route']]: true,
                          [styles.inactive]: activeCity !== tab.name
                        })}
                        id={`tab-${tab.slug}`}
                        onClick={() => setActiveCity(tab.name)}
                        onKeyDown={(event) => handleKeyDown(event, tab.name, 'routes')}
                        role="tab"
                        tabIndex={activeCity === tab.name ? 0 : -1}
                        type="button"
                      >
                        <span>{tab.name}</span>
                      </button>
                    </div>
                    <div
                      className={classNames({
                        [styles.line]: true,
                        [styles['popular-route']]: true,
                        [styles.inactive]: activeCity !== tab.name
                      })}
                    ></div>
                  </div>
                </Fragment>
              ))}
            </div>
            <div
              aria-labelledby={`tab-${popularRoutes.tabs.find((tab) => tab.name === activeCity)?.slug}`}
              className={styles.content}
              id={`tabpanel-${popularRoutes.tabs.find((tab) => tab.name === activeCity)?.slug}`}
              role="tabpanel"
            >
              {activePopularRoutes &&
                Object.entries(activePopularRoutes).map(([_, values]) => (
                  <ul className={styles.column} key={_}>
                    {values.map((value, _) => (
                      <li key={_}>
                        <a className={`${styles.route} gtm-most_popular_bus_routes`} href={value.url} target="_blank">
                          {value.from} <span className={styles.to}>{t('to')}</span> {value.to}
                        </a>
                      </li>
                    ))}
                  </ul>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {originCityRoutes?.length || destinationCityRoutes?.length ? renderPopularBusTickets() : null}
      {renderPopularRoutes()}
    </>
  );
};

export default PopularBusTicketsAndRoutes;
